/* eslint-disable no-underscore-dangle */
/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Box, Card, CardContent, Grid, Pagination, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import ScenarioContent from './scenario-content';
import { SCENARIO_CARD_CONTENT_STYLE, SCENARIO_CARD_STYLE } from '../style';
import {
  selectFavoriteScenarios,
  selectLoader,
  selectScenarios,
  selectSearchData,
} from '../selectors';
import { searchActions } from '../slice';
import SearchSkeletons from './search-skeletons';
import SearchResults from './search-results';

const ScenarioList = () => {
  const scenarios = useSelector(selectScenarios);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const organizationId = useSelector(selectOrganizationId);
  const loading = useSelector(selectLoader);
  const searchData = useSelector(selectSearchData);
  const authUser = useSelector(selectAuthUser);
  //
  const [page, setPage] = useState(1);
  const dispatch = useDispatch();
  //
  useEffect(() => {
    dispatch(
      searchActions.getFavoriteScenario({
        organizationId,
        userId: authUser?.id,
      })
    );
  }, []);
  useEffect(() => {
    dispatch(
      searchActions.getScenarios({
        organizationId,
        query: `page=${page}&limit=12`,
      })
    );
  }, [page]);
  //
  const handleItemClick = (event, scenario) => {
    event.stopPropagation();
    dispatch(searchActions.setSelectedScenario(scenario));
  };
  const handleScenarioClick = (scenario) => dispatch(searchActions.setSelectedScenario(scenario));
  const handlePagination = (_, value) => setPage(value);
  //
  if (loading) {
    return <SearchSkeletons />;
  }

  if (searchData?.length) {
    return <SearchResults onItemClick={handleScenarioClick} />;
  }
  //
  return (
    <Grid container spacing={4} px={16} pb={4}>
      {favoriteScenarios?.length ? (
        <Grid item xs={6} sm={3} md={12}>
          <Typography sx={{ fontWeight: 'semi-bold', fontSize: 16 }}>Pinned Scenarios</Typography>
        </Grid>
      ) : null}

      {favoriteScenarios?.map((scenario) => (
        <Grid item xs={6} sm={3} key={scenario.id}>
          <Box onClick={(e) => handleItemClick(e, scenario)}>
            <Card sx={SCENARIO_CARD_STYLE}>
              <CardContent sx={SCENARIO_CARD_CONTENT_STYLE}>
                <ScenarioContent scenario={scenario} />
              </CardContent>
            </Card>
          </Box>
        </Grid>
      ))}
      <Grid item xs={6} sm={3} md={12}>
        <Typography sx={{ fontWeight: 'semi-bold', fontSize: 16 }}>All Scenarios</Typography>
      </Grid>

      {scenarios?.results?.map((scenario) => (
        <Grid item xs={6} sm={3} key={scenario.id}>
          <Box onClick={(e) => handleItemClick(e, scenario)}>
            <Card sx={SCENARIO_CARD_STYLE}>
              <CardContent sx={SCENARIO_CARD_CONTENT_STYLE}>
                <ScenarioContent scenario={scenario} />
              </CardContent>
            </Card>
          </Box>
        </Grid>
      ))}
      {scenarios?.results ? (
        <Grid item xs={6} sm={3} md={12} container justifyContent="center" mt={8}>
          <Pagination page={page} count={scenarios?.totalPages} onChange={handlePagination} />
        </Grid>
      ) : null}
    </Grid>
  );
};
export default ScenarioList;
